/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Pavel Reznak (reznak@cadwork.cz)
 */

// jscs:disable requireCamelCaseOrUpperCaseIdentifiers

import Pass from "App/render/webgl/passes/pass";
import WebGLRenderGraph from "App/render/webgl/render_graph";

import ShadowMapDepthShader from "App/render/webgl/shaders/shadow_map/shadow_map_depth";
import {Containers, Factory} from "App/utils/factory";
import View from "App/view/view";
import {Framebuffer} from "App/utils/frame_buffer";
import {
    OpaqueBinTypeName,
    OpaqueNoTextureBinTypeName
} from "App/render/webgl/nodes/bins/type_names";
import IScene from "js_interface/src/scene_interface";

/**
 * Create SSAO pass : extend Pass
 *
 * @param {SceneAPI.Scene} aSceneAPI
 * @param {WebGLRenderGraph} aRenderGraph
 * @class
 * @extends Pass
 */
export default
class ShadowMap extends Pass {

    public static TypeName: string = "ShadowMap";

    constructor(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph) {
        super(aSceneAPI, aRenderGraph);

        this.initialize();
    }

    public static create(aSceneAPI: IScene, aRenderGraph: WebGLRenderGraph): ShadowMap {
        return new ShadowMap(aSceneAPI, aRenderGraph);
    }

    /**
     * Initialize values
     */
    public initialize() {
        let shaders = this._renderGraph.getLayer('shadowMaps').getShaders();
        this.attachShaderToBin(OpaqueBinTypeName, shaders.get(ShadowMapDepthShader.TypeName));
        this.attachShaderToBin(OpaqueNoTextureBinTypeName, shaders.get(ShadowMapDepthShader.TypeName));
    }

    /**
     * Render depth from light position to aOutputBuffer
     *
     * aOutputBuffer needs only 1 Float value to store (function floatToVec4 in fragment shader)
     *
     * @param {View}              aView
     * @param {Framebuffer} aOutput     Unsigned byte, RGBA         ... floatToVec4
     */
    public render(aView: View, aOutput: Framebuffer) {
        /* jshint camelcase:false */
        this.view = aView;
        this.state.resetRenderNodesFilters();

        /** @type WebGLRenderingContext */
        let gl = this._context.getWebGLRenderingContext();
        let camera = aView.getCamera();
        let size = this._renderGraph.getLayer('shadowMaps').getTextureSize();

        aOutput.bind();

        // Peter panning fix
        let faceCullingEnabled = gl.getParameter(gl.CULL_FACE);
        let faceCullingMode = gl.getParameter(gl.CULL_FACE_MODE);
        if (!faceCullingEnabled) {
            gl.enable(gl.CULL_FACE);
            gl.frontFace(gl.CCW);
        }
        gl.cullFace(gl.FRONT);

        gl.enable(gl.DEPTH_TEST);
        gl.enable(gl.SCISSOR_TEST);
        gl.viewport(0, 0, size, size);
        gl.scissor(0, 0, size, size);
        gl.clear(gl.COLOR_BUFFER_BIT | gl.DEPTH_BUFFER_BIT);

        // render all the bins related to the geometry pass
        let bins = this._renderGraph.getRenderBins();
        this.state.resetRenderNodesFilters();

        // Set orthogonal projection matrix from light
        this.bindShader(OpaqueBinTypeName);

        // Orthogonal projection matrix from light
        gl.uniformMatrix4fv(this._activeShaderRenderable.getUniformLocations().u_light_matrix, false, camera.getLightOrthogonalMatrix());

        bins.get(OpaqueBinTypeName).render(this);
        bins.get(OpaqueNoTextureBinTypeName).render(this);

        this.view = null;

        // Restore culling
        if (!faceCullingEnabled) {
            gl.disable(gl.CULL_FACE);
        }
        gl.cullFace(faceCullingMode);
    }
}

Factory.register(Containers.PASSES_SM, ShadowMap);
